"use client";
import { AspectRatio } from "../ui/aspect-ratio";
import { Reveal } from "../reveal";
import { Button } from "../ui/button";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { iconsMap } from "../icons";
import Link from "next/link";

import { ExtractBySectionType, SectionType } from "types";
import { PortableText, PortableTextReactComponents } from "next-sanity";
import { cfUrlFor } from "@/sanity/lib/utils";
import { getURL } from "@/lib/utils";

export type HeroSectionType = ExtractBySectionType<SectionType, "hero", "_type">;

const H1 = ({ children }: { children?: React.ReactNode }) => (
  <h1 className="text-start lg:text-center font-semibold text-3xl sm:text-5xl lg:text-7xl leading-tight tracking-tight">
    {children}
  </h1>
);

const H2 = ({ children }: { children?: React.ReactNode }) => (
  <h2 className="text-start lg:text-center max-w-3xl mx-auto font-medium text-xl sm:text-2xl lg:text-3xl leading-snug mt-6">
    {children}
  </h2>
);

const H3 = ({ children }: { children?: React.ReactNode }) => (
  <h3 className="text-start lg:text-center max-w-3xl mx-auto font-medium text-lg sm:text-xl lg:text-2xl leading-snug mt-5">
    {children}
  </h3>
);

const H4 = ({ children }: { children?: React.ReactNode }) => (
  <h4 className="text-start lg:text-center max-w-2xl mx-auto font-medium text-base sm:text-lg lg:text-xl mt-4">
    {children}
  </h4>
);

const H5 = ({ children }: { children?: React.ReactNode }) => (
  <h5 className="text-start lg:text-center max-w-2xl mx-auto font-medium text-sm sm:text-base lg:text-lg uppercase tracking-wide mt-3">
    {children}
  </h5>
);

const H6 = ({ children }: { children?: React.ReactNode }) => (
  <h6 className="text-start lg:text-center max-w-2xl mx-auto font-medium text-xs sm:text-sm lg:text-base tracking-wide mt-2 opacity-80">
    {children}
  </h6>
);

const components: Partial<PortableTextReactComponents> = {
  block: {
    h1: H1,
    h2: H2,
    h3: H3,
    h4: H4,
    h5: H5,
    h6: H6,
    normal: ({ children }) => (
      <p className="text-start lg:text-center max-w-2xl mx-auto font-medium text-lg">{children}.</p>
    ),
  },
};

export function Hero({ data }: { data: HeroSectionType }) {
  const { title, subtitle, description, ctas, mainImage } = data;
  const imageUrl = mainImage ? cfUrlFor(mainImage) : "";
  const isMobile = useMediaQuery("(max-width: 768px)");
  return (
    <section
      style={{
        backgroundImage: `linear-gradient(to top left, transparent, rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('${imageUrl}')`,
      }}
      className="-top-[84px] -mb-[4px] relative w-full bg-center md:bg-left-top bg-cover mx-auto bg-no-repeat lg:h-screen py-20 md:py-0"
    >
      <AspectRatio ratio={isMobile ? 395 / 518 : 16 / 9}>
        <Reveal className="h-full flex items-center justify-center">
          <div className="relative grid items-center justify-center text-primary-text-50 max-w-screen-2xl mx-auto gap-6 px-6">
            <PortableText value={title} components={components} />
            {subtitle && <PortableText value={subtitle} components={components} />}
            {description && <PortableText value={description} components={components} />}
            <div
              className="
              pt-4 sm:pt-6 flex flex-row flex-wrap gap-3 sm:gap-4 
              items-start justify-start sm:items-center sm:justify-center
            "
            >
              {ctas &&
                ctas.map((cta, index) => {
                  const link = getURL(cta);
                  return (
                    <Button
                      variant={cta.variant}
                      radius="full"
                      className="w-full xs:w-fit mt-4"
                      asChild={true}
                      size={"xl"}
                      key={index}
                    >
                      <Link
                        href={link}
                        target={cta.openInNewTab ? "_blank" : "_self"}
                        rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                      >
                        {cta.textBeforeIcon}
                        {cta.icon && iconsMap[cta.icon]}
                        {cta.textAfterIcon}
                      </Link>
                    </Button>
                  );
                })}
            </div>
          </div>
        </Reveal>
      </AspectRatio>
    </section>
  );
}
